// Admin config for the Event Dashboard — its own sidebar tab (Configure group).
// Lives in the ADMIN shell, NOT inside the standalone dashboard. Edits the
// per-tenant config (enable flag, Universe host id, ticket model) via the
// feature-owned /api/tenants/<slug>/event-dashboard/config routes; shows a
// read-only status panel from .../health.
//
// The ticket model is a direct-manipulation editor: the tenant's REAL live
// rate names (from the warm cache via …/rate-names) sit in a tray, each
// colored by where the draft model classifies it — DRAG one onto a cell in
// the card preview to pin it there (an exact-name `match` override that beats
// the keyword rules). Click a preview cell to edit its label/color; drag a
// row's grip to reorder the card's rows. Keyword vocabulary (chips) remains
// as the fallback rules for future rate names.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay, pointerWithin,
} from '@dnd-kit/core';
import { GripVertical } from 'lucide-react';
import { apiFetch } from '@/lib/api.js';
import {
  DEFAULT_TICKET_MODEL, COLOR_PALETTE, paletteIndexOf, classifyRate, isComp,
  matchOverrideKey, entryRowOrder,
} from './eventDashboard/utils/ticketModel.js';
import { normalizeTierName } from './eventDashboard/utils/helpers.js';

const clone = (o) => JSON.parse(JSON.stringify(o));
// The dashboard classifies normalizeTierName(name).toLowerCase() — match
// overrides must store exactly that form to take effect.
const classifyKey = (rawName) => normalizeTierName(rawName).toLowerCase();
const classify = (rawName, model) => {
  const k = classifyKey(rawName);
  return classifyRate(k, model) || (isComp(k, model) ? 'comp' : null);
};

export default function EventDashboardConfig() {
  const { tenantSlug } = useParams();
  const [cfg, setCfg] = useState(null);
  const [health, setHealth] = useState(null);
  const [rateNames, setRateNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/event-dashboard/config`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setCfg(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    apiFetch(`/api/tenants/${tenantSlug}/event-dashboard/health`)
      .then((r) => (r.ok ? r.json() : null)).then(setHealth).catch(() => {});
    apiFetch(`/api/tenants/${tenantSlug}/event-dashboard/rate-names`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setRateNames(d?.rateNames || [])).catch(() => {});
  };
  useEffect(load, [tenantSlug]);

  const editing = !!draft;
  useEffect(() => {
    if (!editing) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [editing]);

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error && !cfg) return <div className="text-destructive">Error: {error}</div>;
  if (!cfg) return null;

  const startEdit = () => setDraft({
    enabled: cfg.enabled,
    universeHostId: cfg.universeHostId || '',
    ticketModel: cfg.ticketModel ? clone(cfg.ticketModel) : null,
  });

  const validation = editing ? validateDraft(draft) : { errors: [], warnings: [] };

  const save = async () => {
    setError(null);
    const host = draft.universeHostId.trim();
    if (host && !/^[a-f0-9]{24}$/i.test(host)) {
      setError('Universe host ID must be a 24-char hex ObjectId (or blank).'); return;
    }
    if (validation.errors.length) { setError(validation.errors[0]); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/event-dashboard/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: draft.enabled, universeHostId: host || null, ticketModel: draft.ticketModel }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'save_failed');
      setCfg(d); setDraft(null); load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  // Two-step confirm (window.confirm is prohibited — blocking native dialog):
  // first click arms the button for 4s, second click resets.
  const [resetArmed, setResetArmed] = useState(false);
  const resetToDefault = () => {
    if (!resetArmed) {
      setResetArmed(true);
      setTimeout(() => setResetArmed(false), 4000);
      return;
    }
    setResetArmed(false);
    setDraft((d) => ({ ...d, ticketModel: null }));
  };

  const v = draft;
  const shownModel = editing ? draft.ticketModel : cfg.ticketModel;
  const effectiveModel = shownModel || DEFAULT_TICKET_MODEL;
  // Any edit (drag, popover, vocab) on a tenant still on the default first
  // materializes the draft model from the default.
  const changeModel = (next) => setDraft((d) => ({ ...d, ticketModel: next }));

  const saveButtons = (
    <div className="flex gap-2">
      <button onClick={() => { setDraft(null); setError(null); }} disabled={saving} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">Cancel</button>
      <button onClick={save} disabled={saving || validation.errors.length > 0} className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
    </div>
  );

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Box Office Live config · {tenantSlug}</h1>
        {editing ? saveButtons : (
          <button onClick={startEdit} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent">Edit</button>
        )}
      </div>

      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Setup</h2>
        <div className="border border-border rounded-md p-3 bg-card space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-40 text-muted-foreground">Enabled</span>
            {editing ? (
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!v.enabled} onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))} />
                <span>{v.enabled ? 'on, dashboard tab shows for this tenant' : 'off'}</span>
              </label>
            ) : (cfg.enabled ? 'YES, dashboard tab is live' : 'no')}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-40 text-muted-foreground">Universe host ID</span>
            {editing ? (
              <input value={v.universeHostId} onChange={(e) => setDraft((d) => ({ ...d, universeHostId: e.target.value }))}
                placeholder="24-char hex ObjectId" className="flex-1 px-2 py-1 border border-input rounded-md bg-background font-mono text-xs" />
            ) : (cfg.universeHostId ? <code className="text-xs">{cfg.universeHostId}</code> : <span className="text-muted-foreground italic">not set</span>)}
          </div>
          <p className="text-xs text-muted-foreground">The host ID is the GraphQL <code>hostId</code> the dashboard queries Universe with. Enabling without a host ID leaves the dashboard non-functional.</p>
        </div>
      </section>

      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Ticket model</h2>
          {editing && shownModel !== null && (
            <button
              onClick={resetToDefault}
              title="Discards custom labels, colors, keywords, and drag-assigned rates when you save"
              className={`text-xs px-2 py-0.5 border rounded ${resetArmed ? 'border-red-500 bg-red-500/10 text-red-600 font-medium' : 'border-input hover:bg-accent'}`}
            >{resetArmed ? 'Click again to confirm reset' : 'Reset to default'}</button>
          )}
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          {shownModel === null && (
            <div className="text-xs text-muted-foreground mb-3">
              Using the <strong>built-in default</strong> model (LIV gender-split tiers).
              {editing && ' Any change below (drag a rate, click a cell, edit a keyword) creates this tenant’s own copy.'}
            </div>
          )}

          {editing ? (
            <ModelWorkbench
              model={effectiveModel}
              onChange={changeModel}
              rateNames={rateNames}
              warnings={validation.warnings}
            />
          ) : (
            <>
              {shownModel !== null && <TicketModelSummary model={shownModel} />}
              <div className="mt-3 pt-3 border-t border-border grid md:grid-cols-2 gap-4">
                <RateTester model={effectiveModel} />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Card preview <span className="normal-case">(sample numbers)</span></div>
                  <CardPreview model={effectiveModel} />
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {health && (
        <section className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Live status</h2>
          <div className="border border-border rounded-md p-3 bg-card grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <Stat label="Warmer" value={health.warmer?.started ? `last run ${health.warmer.lastDurationMs ?? '—'}ms · ${health.warmer.consecutiveErrors || 0} errors` : 'not started'} />
            <Stat label="Warmed (this tenant)" value={fmtWarm(health.warmer?.tenants?.[tenantSlug])} />
            <Stat label="WebSocket" value={`${health.websocket?.total_connections ?? 0} conn · ${health.websocket?.active_subscriptions ?? 0} subs`} />
            <Stat label="Mode" value={health.mode || '—'} />
          </div>
        </section>
      )}

      {editing && (
        <div className="sticky bottom-0 -mx-2 px-2 py-2 bg-background/95 backdrop-blur border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {validation.errors.length ? <span className="text-destructive">{validation.errors[0]}</span> : 'Unsaved changes'}
          </span>
          {saveButtons}
        </div>
      )}
    </div>
  );
}

// ===== Validation =====

function validateDraft(draft) {
  const errors = []; const warnings = [];
  const tm = draft.ticketModel;
  if (!tm) return { errors, warnings };
  for (const c of tm.categories) {
    if (!String(c.label || '').trim()) errors.push(`The "${c.key}" category has an empty label.`);
  }
  const overlap = (a = [], b = []) => a.filter((w) => b.includes(w));
  const fm = overlap(tm.vocab.female, tm.vocab.male);
  if (fm.length) warnings.push(`"${fm.join('", "')}" appears in BOTH the female and male lists, rates containing it will classify ambiguously.`);
  const eg = overlap(tm.vocab.early, tm.vocab.general);
  if (eg.length) warnings.push(`"${eg.join('", "')}" appears in BOTH the early-arrival and general-admission lists, matching rates will classify ambiguously.`);
  return { errors, warnings };
}

// ===== Model mutations (pure) =====

// Pin a (classify-keyed) rate name to one category; unpin it everywhere else.
function assignMatch(model, key, catKey) {
  return {
    ...model,
    categories: model.categories.map((c) => {
      const match = (c.match || []).filter((n) => n !== key);
      if (c.key === catKey) match.push(key);
      return { ...c, ...(match.length ? { match } : { match: undefined }) };
    }),
  };
}

function removeMatch(model, key) {
  return {
    ...model,
    categories: model.categories.map((c) => {
      const match = (c.match || []).filter((n) => n !== key);
      return { ...c, ...(match.length ? { match } : { match: undefined }) };
    }),
  };
}

// Group tier-ladder duplicates — names identical except a "Tier N" number
// (price laddering of the same rate) collapse into one draggable chip labeled
// with the tier range. Dragging the group pins ALL member names.
const TIER_RE = /\btier\s*\d+\b/i;
const tierKeyOf = (name) => name.replace(/\btier\s*\d+\b/gi, 'tier#').toLowerCase();
function tierRangeLabel(names) {
  const nums = names
    .map((n) => parseInt((n.match(/\btier\s*(\d+)\b/i) || [])[1], 10))
    .filter(Number.isFinite).sort((a, b) => a - b);
  if (!nums.length) return names[0];
  return names[0].replace(TIER_RE, `Tier ${nums[0]}–${nums[nums.length - 1]}`);
}
function groupRateNames(rateNames) {
  const map = new Map();
  for (const r of rateNames) {
    const key = tierKeyOf(r.name);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return [...map.values()].map((members) => (
    members.length === 1
      ? { label: members[0].name, names: [members[0].name], events: members[0].events }
      : { label: tierRangeLabel(members.map((m) => m.name)), names: members.map((m) => m.name), events: Math.max(...members.map((m) => m.events)) }
  ));
}

// Move the dragged entry-type's row to the target row's position; categories
// are rebuilt grouped by the new entry order (stable within each group).
function reorderRows(model, fromEntry, toEntry) {
  const order = entryRowOrder(model);
  const from = order.indexOf(fromEntry); const to = order.indexOf(toEntry);
  if (from === -1 || to === -1 || from === to) return model;
  order.splice(to, 0, order.splice(from, 1)[0]);
  return { ...model, categories: order.flatMap((e) => model.categories.filter((c) => c.entry === e)) };
}

// ===== The edit-mode workbench: tray + editable preview + vocab =====

function ModelWorkbench({ model, onChange, rateNames, warnings }) {
  const [dragging, setDragging] = useState(null); // label of the chip in flight
  const [editCatKey, setEditCatKey] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const groups = groupRateNames(rateNames);

  const onDragEnd = ({ active, over }) => {
    setDragging(null);
    if (!over) return;
    const a = String(active.id); const o = String(over.id);
    if (a.startsWith('rate:') && o.startsWith('cat:')) {
      const group = groups[Number(a.slice(5))];
      if (!group) return;
      let next = model;
      for (const n of group.names) next = assignMatch(next, classifyKey(n), o.slice(4));
      onChange(next);
    } else if (a.startsWith('row:') && o.startsWith('row:') && a !== o) {
      onChange(reorderRows(model, a.slice(4), o.slice(4)));
    }
  };

  const editCat = editCatKey ? model.categories.find((c) => c.key === editCatKey) : null;
  const setCat = (key, patch) => onChange({ ...model, categories: model.categories.map((c) => (c.key === key ? { ...c, ...patch } : c)) });

  // Pinned (match) entries, tier-grouped per target category for display.
  const pinGroups = (() => {
    const map = new Map();
    for (const c of model.categories) {
      for (const n of (c.match || [])) {
        const key = `${tierKeyOf(n)}→${c.key}`;
        if (!map.has(key)) map.set(key, { names: [], cat: c });
        map.get(key).names.push(n);
      }
    }
    return [...map.values()].map((g) => ({ ...g, label: g.names.length === 1 ? g.names[0] : tierRangeLabel(g.names) }));
  })();

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={({ active }) => { const id = String(active.id); if (id.startsWith('rate:')) setDragging(groups[Number(id.slice(5))]?.label || null); }} onDragEnd={onDragEnd}>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
            Your live rate names <span className="normal-case">· drag onto a cell to pin it there</span>
          </div>
          {rateNames.length === 0 ? (
            <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3">
              No cached rate names yet (the cache warms within ~2 minutes of the dashboard being enabled).
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto border border-border rounded-md p-2">
              {groups.map((g, i) => (
                <RateChip key={g.label} group={g} idx={i} model={model} />
              ))}
            </div>
          )}
          <div className="mt-1 text-[10px] text-muted-foreground">Chip color = where it classifies right now. Gray dashed = unmatched (shows only under Sales by Tier).</div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
            Card preview <span className="normal-case">· drop rates on cells · click a cell to edit · drag <GripVertical className="w-2.5 h-2.5 inline" /> to reorder rows</span>
          </div>
          <CardPreview model={model} editable rateDragging={!!dragging} onCellClick={(k) => setEditCatKey(k === editCatKey ? null : k)} activeKey={editCatKey} />
          {editCat && (
            <div className="mt-2 border border-primary/40 rounded-md p-2 bg-background">
              <div className="flex items-center gap-2">
                <input
                  value={editCat.label}
                  onChange={(e) => setCat(editCat.key, { label: e.target.value })}
                  className={`flex-1 px-2 py-1 border rounded-md bg-background text-xs ${String(editCat.label || '').trim() ? 'border-input' : 'border-destructive'}`}
                />
                <SwatchPicker color={editCat.color} onChange={(color) => setCat(editCat.key, { color })} />
                <button onClick={() => setEditCatKey(null)} className="text-xs px-2 py-0.5 border border-input rounded hover:bg-accent">Done</button>
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">{explainCategory(editCat)}</div>
            </div>
          )}
        </div>
      </div>

      {pinGroups.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Pinned rates <span className="normal-case">· exact-name assignments that beat the keyword rules</span></div>
          <div className="flex flex-wrap gap-1.5">
            {pinGroups.map((g) => (
              <span key={`${g.label}-${g.cat.key}`} className={`${g.cat.color.text} ${g.cat.color.bg} inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs`}>
                <span className="font-mono">{g.label}</span>
                {g.names.length > 1 && <span className="opacity-70">×{g.names.length}</span>}
                <span className="opacity-70">→ {g.cat.label}</span>
                <button type="button" onClick={() => onChange(g.names.reduce((m, n) => removeMatch(m, n), model))} className="opacity-70 hover:opacity-100 leading-none">×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      <VocabEditor model={model} onChange={onChange} warnings={warnings} />

      <div className="mt-4 pt-3 border-t border-border max-w-sm">
        <RateTester model={model} />
      </div>

      <DragOverlay>
        {dragging && <span className="px-2 py-0.5 rounded text-xs font-mono bg-primary text-primary-foreground shadow-lg">{dragging}</span>}
      </DragOverlay>
    </DndContext>
  );
}

function RateChip({ group, idx, model }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `rate:${idx}` });
  // A group chip's color = the members' classification when they all agree
  // (the normal case — tier ladders classify identically); 'mixed' otherwise.
  const keys = group.names.map((n) => classify(n, model));
  const allSame = keys.every((k) => k === keys[0]);
  const cat = allSame && keys[0] ? model.categories.find((c) => c.key === keys[0]) : null;
  const mixed = !allSame;
  const pinnedCount = group.names.filter((n) => matchOverrideKey(classifyKey(n), model) !== null).length;
  const tone = cat ? `${cat.color.text} ${cat.color.bg}` : 'text-muted-foreground border border-dashed border-border';
  const where = mixed ? 'mixed (members classify differently)' : (cat ? cat.label : 'unmatched');
  return (
    <span
      ref={setNodeRef} {...listeners} {...attributes}
      title={`${where}${pinnedCount ? ` · ${pinnedCount} pinned` : ''} · in ${group.events} event${group.events === 1 ? '' : 's'}${group.names.length > 1 ? ` · ${group.names.length} tiers` : ''}`}
      className={`${tone} px-2 py-0.5 rounded text-xs font-mono cursor-grab select-none inline-flex items-center gap-1 ${isDragging ? 'opacity-30' : ''}`}
    >
      {pinnedCount > 0 && '📌'}{group.label}
      {group.names.length > 1 && <span className="opacity-60 text-[10px]">×{group.names.length}</span>}
    </span>
  );
}

function VocabEditor({ model, onChange, warnings }) {
  const setVocab = (key, val) => onChange({ ...model, vocab: { ...model.vocab, [key]: val } });
  const FIELDS = [
    { key: 'early', label: '“Early arrival” keywords', array: true },
    { key: 'general', label: '“General admission” keywords', array: true },
    { key: 'female', label: 'Female keywords', array: true },
    { key: 'male', label: 'Male keywords', array: true },
    { key: 'earlyLoose', label: 'Loose “early” token' },
    { key: 'rsvp', label: 'RSVP keyword' },
    { key: 'vip', label: 'VIP keyword' },
    { key: 'comp', label: 'Comp keyword' },
  ];
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Keyword rules <span className="normal-case">· fallback classification for rate names that aren't pinned (case-insensitive).</span></div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {FIELDS.map((f) => {
          const raw = model.vocab[f.key];
          return (
            <label key={f.key} className="text-xs">
              <span className="block text-muted-foreground mb-0.5">{f.label}</span>
              {f.array ? (
                <ChipInput values={Array.isArray(raw) ? raw : []} onChange={(vals) => setVocab(f.key, vals)} placeholder="add keyword…" />
              ) : (
                <input value={raw || ''} onChange={(e) => setVocab(f.key, e.target.value.toLowerCase())}
                  className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono" />
              )}
            </label>
          );
        })}
      </div>
      {warnings.map((w) => (
        <div key={w} className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">⚠ {w}</div>
      ))}
    </div>
  );
}

function ChipInput({ values, onChange, placeholder }) {
  const [text, setText] = useState('');
  const add = () => {
    const w = text.trim().toLowerCase();
    if (w && !values.includes(w)) onChange([...values, w]);
    setText('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1 border border-input rounded-md bg-background min-h-[28px]">
      {values.map((w) => (
        <span key={w} className="inline-flex items-center gap-1 bg-muted px-1.5 py-0.5 rounded font-mono">
          {w}
          <button type="button" onClick={() => onChange(values.filter((x) => x !== w))} className="text-muted-foreground hover:text-foreground leading-none">×</button>
        </span>
      ))}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          else if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1));
        }}
        onBlur={add}
        placeholder={values.length ? '' : placeholder}
        className="flex-1 min-w-[70px] bg-transparent outline-none font-mono"
      />
    </div>
  );
}

function SwatchPicker({ color, onChange }) {
  const idx = paletteIndexOf(color);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {COLOR_PALETTE.map((p, i) => (
        <button
          key={p.name} type="button" title={p.name}
          onClick={() => onChange({ text: p.text, bg: p.bg })}
          className={`w-5 h-5 rounded-full ${p.bg} ${p.text} flex items-center justify-center border ${i === idx ? 'ring-2 ring-primary ring-offset-1 border-transparent' : 'border-border'}`}
        >
          <span className="w-2 h-2 rounded-full bg-current"></span>
        </button>
      ))}
      {idx === -1 && <span className="text-[10px] text-muted-foreground ml-1">custom</span>}
    </div>
  );
}

function RateTester({ model }) {
  const [name, setName] = useState('');
  const key = name.trim() ? classify(name, model) : null;
  const cat = key ? model.categories.find((c) => c.key === key) : null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Try a rate name</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Early Arrival Female"
        className="w-full px-2 py-1 border border-input rounded-md bg-background text-xs"
      />
      <div className="mt-1.5 min-h-[22px] text-xs">
        {name.trim() && (cat ? (
          <span>→ <span className={`${cat.color.text} ${cat.color.bg} px-2 py-0.5 rounded font-medium`}>{cat.label}</span></span>
        ) : (
          <span className="text-muted-foreground">→ no category, appears only under “Sales by Tier”.</span>
        ))}
      </div>
    </div>
  );
}

// ===== Card preview — static in view mode; droppable/clickable/reorderable in edit =====

const SAMPLE_VALUES = { rsvp: ['274/350', '118/350'], early: ['$30 · 45', '$40 · 38'], general: ['$50 · 112', '$60 · 95'], vip: ['$150 · 12'], comp: ['18'] };

function CardPreview({ model, editable = false, rateDragging = false, onCellClick, activeKey }) {
  const rows = entryRowOrder(model);
  return (
    <div className="border border-border rounded-md p-2 bg-white dark:bg-gray-800">
      {rows.map((entry) => {
        const cats = model.categories.filter((c) => c.entry === entry);
        if (!cats.length) return null;
        return (
          <PreviewRow key={entry} entry={entry} editable={editable} rateDragging={rateDragging}>
            <div className={`flex-1 grid ${cats.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-1.5`}>
              {cats.map((c, i) => (
                <PreviewCell key={c.key} cat={c} value={(SAMPLE_VALUES[entry] || [])[i] || '—'} wide={cats.length === 1}
                  editable={editable} onClick={onCellClick} active={activeKey === c.key} />
              ))}
            </div>
          </PreviewRow>
        );
      })}
    </div>
  );
}

function PreviewRow({ entry, editable, rateDragging, children }) {
  const drag = useDraggable({ id: `row:${entry}`, disabled: !editable });
  // Row droppables only accept ROW drags — while a rate chip is in flight they
  // are disabled so the nested cell droppables receive the drop.
  const drop = useDroppable({ id: `row:${entry}`, disabled: !editable || rateDragging });
  return (
    <div ref={drop.setNodeRef} className={`flex items-center gap-1 mb-1.5 last:mb-0 ${drop.isOver ? 'outline outline-2 outline-primary/40 rounded' : ''} ${drag.isDragging ? 'opacity-40' : ''}`}>
      {editable && (
        <span ref={drag.setNodeRef} {...drag.listeners} {...drag.attributes} className="cursor-grab text-muted-foreground hover:text-foreground">
          <GripVertical className="w-3 h-3" />
        </span>
      )}
      {children}
    </div>
  );
}

function PreviewCell({ cat, value, wide, editable, onClick, active }) {
  const { setNodeRef, isOver } = useDroppable({ id: `cat:${cat.key}`, disabled: !editable });
  return (
    <div
      ref={setNodeRef}
      onClick={editable ? () => onClick(cat.key) : undefined}
      className={`${cat.color.bg} p-1.5 rounded flex items-center justify-between gap-1 text-[10px] ${wide ? 'px-3' : ''} ${editable ? 'cursor-pointer' : ''} ${isOver ? 'ring-2 ring-primary' : ''} ${active ? 'ring-2 ring-primary/60' : ''}`}
    >
      <span className={`${cat.color.text} font-medium whitespace-nowrap`}>{cat.label || cat.key}:</span>
      <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">{value}</span>
    </div>
  );
}

function explainCategory(cat) {
  const entry = {
    early: 'an early-arrival keyword',
    general: 'a general-admission keyword',
    rsvp: 'the RSVP keyword (free)',
    vip: 'the VIP keyword',
    comp: 'the comp keyword (free)',
  }[cat.entry] || cat.entry;
  return cat.gender
    ? `Catches rates with a ${cat.gender} keyword + ${entry}.`
    : `Catches rates with ${entry}.`;
}

function TicketModelSummary({ model }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {model.categories.map((cat) => (
          <span key={cat.key} className={`${cat.color.text} ${cat.color.bg} px-2 py-0.5 rounded text-xs font-medium`}>{cat.label}</span>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground">
        Custom model · {model.categories.length} categories
        {model.categories.some((c) => c.match?.length) && ` · ${model.categories.reduce((n, c) => n + (c.match?.length || 0), 0)} pinned rate(s)`}
        {' '}· keywords: {Object.entries(model.vocab).map(([k, val]) => `${k}=${Array.isArray(val) ? val.join('/') : val}`).join(' · ')}
      </div>
    </div>
  );
}

function fmtWarm(t) {
  if (!t) return '—';
  if (t.error) return `error: ${t.error}`;
  return `${t.warmed}/${t.events} warmed${t.failed ? ` · ${t.failed} failed` : ''}`;
}

function Stat({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
